import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Star, Trash2, X } from 'lucide-react';
import { api, formatApiError } from '../lib/api';

const STATUS_LABEL = {
    pending: 'În așteptare',
    approved: 'Publicat',
    rejected: 'Respins',
};

function formatDateTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('ro-RO', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

export function PlatformTestimonialsPanel({ onChange }) {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ quote: '', author_name: '', author_role: '', rating: 5 });
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        api.get('/admin/testimonials')
            .then((r) => {
                setItems(r.data.items || []);
                onChange?.();
            })
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (filter === 'all') return items;
        return items.filter((t) => t.status === filter);
    }, [items, filter]);

    const counts = useMemo(() => ({
        all: items.length,
        pending: items.filter((t) => t.status === 'pending').length,
        approved: items.filter((t) => t.status === 'approved').length,
        rejected: items.filter((t) => t.status === 'rejected').length,
    }), [items]);

    const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const create = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            await api.post('/admin/testimonials', {
                ...form,
                rating: Number(form.rating) || 5,
            });
            setForm({ quote: '', author_name: '', author_role: '', rating: 5 });
            setShowForm(false);
            load();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const setStatus = async (id, status) => {
        await api.put(`/admin/testimonials/${id}/status`, { status });
        load();
    };

    const remove = async (id) => {
        if (!window.confirm('Ștergi acest testimonial?')) return;
        await api.delete(`/admin/testimonials/${id}`);
        load();
    };

    return (
        <div className="panel admin-panel" data-testid="sa-testimonials">
            <div className="admin-panel__toolbar">
                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', margin: 0 }}>
                    Testimoniale de la restaurante (după 30 zile) și postări platformă — apar pe homepage în banner derulant.
                </p>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => { setShowForm((v) => !v); setError(''); }}>
                    <Plus size={14} /> Adaugă testimonial
                </button>
            </div>

            {showForm && (
                <form onSubmit={create} className="panel" style={{ marginBottom: '1rem', padding: '1rem', background: 'var(--surface-2)' }}>
                    {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginBottom: '0.75rem' }}>{error}</div>}
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                        <div>
                            <label className="label" htmlFor="pt-quote">Testimonial</label>
                            <textarea id="pt-quote" className="input" rows={3} required minLength={10} maxLength={500}
                                value={form.quote} onChange={setField('quote')} placeholder="Cită testimonialul..." data-testid="sa-testimonial-quote" />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                            <div>
                                <label className="label" htmlFor="pt-name">Nume</label>
                                <input id="pt-name" className="input" required value={form.author_name} onChange={setField('author_name')} placeholder="Nume P." data-testid="sa-testimonial-name" />
                            </div>
                            <div>
                                <label className="label" htmlFor="pt-role">Funcție / restaurant</label>
                                <input id="pt-role" className="input" required value={form.author_role} onChange={setField('author_role')} placeholder="Owner, Bistro Vintage" data-testid="sa-testimonial-role" />
                            </div>
                            <div>
                                <label className="label" htmlFor="pt-rating">Rating (1-5)</label>
                                <input id="pt-rating" className="input" type="number" min={1} max={5} value={form.rating} onChange={setField('rating')} data-testid="sa-testimonial-rating" />
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.85rem' }}>
                        <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="sa-testimonial-save">
                            {saving ? <span className="spinner" /> : 'Publică pe homepage'}
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Anulează</button>
                    </div>
                </form>
            )}

            <div className="admin-filter-btns" style={{ marginBottom: '1rem' }}>
                {[
                    ['all', 'Toate'],
                    ['pending', 'În așteptare'],
                    ['approved', 'Publicate'],
                    ['rejected', 'Respinse'],
                ].map(([id, label]) => (
                    <button
                        key={id}
                        type="button"
                        className={`btn btn-sm ${filter === id ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setFilter(id)}
                    >
                        {label} ({counts[id] ?? 0})
                    </button>
                ))}
            </div>

            {loading ? (
                <p style={{ color: 'var(--text-muted)', margin: 0 }}>Se încarcă...</p>
            ) : filtered.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', margin: 0 }}>Niciun testimonial.</p>
            ) : (
                <div className="admin-messages">
                    {filtered.map((t) => (
                        <div key={t.id} className={`admin-message admin-message--${t.status}`} data-testid={`sa-testimonial-${t.id}`}>
                            <div className="admin-message__body" style={{ paddingTop: '0.75rem' }}>
                                <div style={{ display: 'flex', gap: 2, marginBottom: '0.5rem' }}>
                                    {[1, 2, 3, 4, 5].map((s) => (
                                        <Star key={s} size={12} fill={s <= (t.rating || 5) ? 'var(--amber)' : 'transparent'} color="var(--amber)" />
                                    ))}
                                </div>
                                <p style={{ margin: '0 0 0.75rem', lineHeight: 1.6 }}>"{t.quote}"</p>
                                <div style={{ fontSize: '0.88rem', marginBottom: '0.35rem' }}>
                                    <strong>{t.author_name}</strong>
                                    <span style={{ color: 'var(--text-muted)' }}> · {t.author_role}</span>
                                </div>
                                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                                    {t.source === 'platform' ? 'Platformă' : t.restaurant_name || 'Restaurant'}
                                    {' · '}{formatDateTime(t.created_at)}
                                    {' · '}
                                    <span className={`badge badge-${t.status === 'approved' ? 'teal' : t.status === 'pending' ? 'cyan' : 'magenta'}`}>
                                        {STATUS_LABEL[t.status]}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    {t.status === 'pending' && (
                                        <>
                                            <button type="button" className="btn btn-primary btn-sm" onClick={() => setStatus(t.id, 'approved')}>
                                                <Check size={12} /> Aprobă
                                            </button>
                                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setStatus(t.id, 'rejected')}>
                                                <X size={12} /> Respinge
                                            </button>
                                        </>
                                    )}
                                    {t.status === 'rejected' && (
                                        <button type="button" className="btn btn-primary btn-sm" onClick={() => setStatus(t.id, 'approved')}>
                                            <Check size={12} /> Aprobă
                                        </button>
                                    )}
                                    {t.status === 'approved' && (
                                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setStatus(t.id, 'rejected')}>
                                            <X size={12} /> Ascunde
                                        </button>
                                    )}
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => remove(t.id)}>
                                        <Trash2 size={12} /> Șterge
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
